import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, switchMap, tap } from 'rxjs/operators';
import { Observable, Subscription, timer, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { StorageService } from './storage.service';
import { PlanService } from './plan.service';
import { HttpOpts, buildHttpContext } from '../http/http-opts';
import {
  AuthToken,
  SendOtpRequest,
  SendOtpResponse,
  UserProfile,
  VerifyOtpRequest,
  normalizeUserProfile,
} from '../models/auth.model';

const PLAN_REFRESH_MS = 3_600_000; // 1h

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly storage = inject(StorageService);
  private readonly planService = inject(PlanService);

  private readonly _currentUser = signal<UserProfile | null>(
    this.normalizeCached(this.storage.getUser<UserProfile>()),
  );

  private planRefreshSub: Subscription | null = null;

  readonly currentUser = this._currentUser.asReadonly();
  readonly isAuthenticated = computed(() => this._currentUser() !== null);

  constructor() {
    const cached = this._currentUser();
    if (cached) {
      if (cached.plan) {
        this.planService.fromAuthMe(cached.plan);
      } else {
        this.planService.clearPlan();
      }
      this.startPlanRefresh();
    }
  }

  sendOtp(email: string, opts?: HttpOpts): Observable<SendOtpResponse> {
    const body: SendOtpRequest = { email };
    return this.http.post<SendOtpResponse>(`${environment.apiUrl}/auth/send-otp`, body, {
      context: buildHttpContext(opts),
    });
  }

  verifyOtp(email: string, code: string, opts?: HttpOpts): Observable<UserProfile> {
    const body: VerifyOtpRequest = { email, code };
    return this.http.post<AuthToken>(`${environment.apiUrl}/auth/verify-otp`, body, {
      context: buildHttpContext(opts),
    }).pipe(
      tap((token) => this.storage.setToken(token.access_token)),
      switchMap(() => this.loadProfile(opts)),
      tap(() => this.startPlanRefresh()),
    );
  }

  loadProfile(opts?: HttpOpts): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${environment.apiUrl}/auth/me`, {
      context: buildHttpContext(opts),
    }).pipe(
      map((user) => normalizeUserProfile(user)),
      tap((user) => {
        this.storage.setUser(user);
        this._currentUser.set(user);
        if (user.plan) {
          this.planService.fromAuthMe(user.plan);
        } else {
          this.planService.clearPlan();
        }
      }),
    );
  }

  logout(): void {
    this.stopPlanRefresh();
    this.storage.clearAll();
    this._currentUser.set(null);
    this.router.navigate(['/auth/login']);
  }

  private normalizeCached(user: UserProfile | null): UserProfile | null {
    if (!user) return null;
    const normalized = normalizeUserProfile(user);
    if (normalized.role !== user.role) {
      this.storage.setUser(normalized);
    }
    return normalized;
  }

  private startPlanRefresh(): void {
    if (this.planRefreshSub) return;
    this.planRefreshSub = timer(PLAN_REFRESH_MS, PLAN_REFRESH_MS).pipe(
      switchMap(() => this.loadProfile({ skipErrorModal: true }).pipe(catchError(() => EMPTY))),
    ).subscribe();
  }

  private stopPlanRefresh(): void {
    this.planRefreshSub?.unsubscribe();
    this.planRefreshSub = null;
  }
}
